/**
 * CP-12.5 — Verification UX.
 *
 * Phase 12 r3 §5.5 + Locks §17/§29/§32:
 *   - Client post-completion verify form (writes ONLY to *_client_verified).
 *   - Admin/Manufacturer grant form (writes ONLY to *_company_verified).
 *   - Contractor's own verification panel + "Request Verification" button.
 *   - Admin verifications observability tab + requests inbox.
 *
 * No ranking-rule changes. No Stripe activation. No Phase 13 work.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

const SUBJECT_KINDS = [
  { value: "equipment", label: "Equipment" },
  { value: "role", label: "Role" },
  { value: "certification", label: "Certification" },
];

const TIERS = ["1-2", "5+", "20+", "100+"];

function LayerBadge({ layer }) {
  if (layer === "company")
    return (
      <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-0.5 text-xs font-medium">
        Company-verified
      </span>
    );
  return (
    <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2.5 py-0.5 text-xs font-medium">
      Client-verified
    </span>
  );
}

// ---------------------------------------------------------------------------
// ClientPostCompletionVerifyForm
// Mounts on completed bookings. Lets the Client click-verify per qualification
// the contractor has self-declared.
// ---------------------------------------------------------------------------

export function ClientPostCompletionVerifyForm({ jobId, bookingId, bookingState, leadUserId }) {
  const [catalogs, setCatalogs] = useState({ equipment: [], roles: [], certs: [] });
  const [contractorSelf, setContractorSelf] = useState(null);
  const [kind, setKind] = useState("equipment");
  const [subjectId, setSubjectId] = useState("");
  const [tier, setTier] = useState("20+");
  const [submitting, setSubmitting] = useState(false);

  const active = bookingState === "completed";

  useEffect(() => {
    if (!active) return;
    (async () => {
      try {
        const [eq, roles, certs] = await Promise.all([
          api("/catalogs/equipment-tags").catch(() => ({ data: [] })),
          api("/catalogs/roles").catch(() => ({ data: [] })),
          api("/catalogs/qualification-certifications").catch(() => ({ data: [] })),
        ]);
        setCatalogs({ equipment: eq.data || [], roles: roles.data || [], certs: certs.data || [] });
      } catch {
        /* noop */
      }
    })();
  }, [active]);

  const options = useMemo(() => {
    if (kind === "equipment") return catalogs.equipment;
    if (kind === "role") return catalogs.roles;
    return catalogs.certs;
  }, [kind, catalogs]);

  async function submit(e) {
    e.preventDefault();
    if (!subjectId) {
      toast.error("Pick the qualification to verify.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        contractor_user_id: leadUserId,
        subject_kind: kind,
        subject_id: Number(subjectId),
      };
      if (kind === "equipment") body.project_count_tier = tier;
      await api(`/jobs/${jobId}/bookings/${bookingId}/verify-contractor`, {
        method: "POST",
        body,
      });
      toast.success("Verification recorded. Thank you.");
      setSubjectId("");
    } catch (err) {
      // Friendly message for the most common 409 paths.
      const code = err?.code;
      if (code === "SELF_CLAIM_MISSING")
        toast.error("Contractor hasn't self-declared this qualification yet.");
      else if (code === "SELF_VERIFY_FORBIDDEN")
        toast.error("Verifier and contractor must be different (Lock §17).");
      else toast.error(err.message || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (!active) return null;
  if (!leadUserId) return null;

  return (
    <form
      onSubmit={submit}
      data-testid={TID.verifClientForm}
      className="avcp-card p-4 space-y-3"
    >
      <div>
        <h3 className="font-semibold">Verify what the Lead delivered</h3>
        <p className="text-xs text-slate-500">
          Confirm equipment, role, or certification you saw them exercise on
          this booking. Adds a Client-verified badge to their profile.
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="grid gap-1 text-sm">
          <span className="font-medium">Type</span>
          <select
            data-testid={TID.verifClientKindSelect}
            className="avcp-input"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setSubjectId("");
            }}
          >
            {SUBJECT_KINDS.map((k) => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm md:col-span-2">
          <span className="font-medium">Item</span>
          <select
            data-testid={TID.verifClientSubjectSelect}
            className="avcp-input"
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
          >
            <option value="">— Select —</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label || o.display_name || o.slug}
              </option>
            ))}
          </select>
        </label>
        {kind === "equipment" && (
          <label className="grid gap-1 text-sm">
            <span className="font-medium">Project count tier</span>
            <select
              data-testid={TID.verifClientTierSelect}
              className="avcp-input"
              value={tier}
              onChange={(e) => setTier(e.target.value)}
            >
              {TIERS.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
          </label>
        )}
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          data-testid={TID.verifClientSubmit}
          className="avcp-btn-primary"
          disabled={submitting}
        >
          {submitting ? "Recording…" : "Record verification"}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Contractor's own verifications panel — mounts on profile/qualification page
// ---------------------------------------------------------------------------

export function MyVerificationsPanel() {
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Request form
  const [catalogs, setCatalogs] = useState({ equipment: [], roles: [], certs: [] });
  const [reqKind, setReqKind] = useState("equipment");
  const [reqSubject, setReqSubject] = useState("");
  const [reqVerifier, setReqVerifier] = useState("manufacturer");

  async function reload() {
    try {
      const [v, r] = await Promise.all([
        api("/me/contractor-profile/verifications"),
        api("/me/contractor-profile/verification-requests"),
      ]);
      setRows(v.data || []);
      setRequests(r.data || []);
    } catch (err) {
      // ignore
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    reload();
    (async () => {
      try {
        const [eq, roles, certs] = await Promise.all([
          api("/catalogs/equipment-tags").catch(() => ({ data: [] })),
          api("/catalogs/roles").catch(() => ({ data: [] })),
          api("/catalogs/qualification-certifications").catch(() => ({ data: [] })),
        ]);
        setCatalogs({ equipment: eq.data || [], roles: roles.data || [], certs: certs.data || [] });
      } catch {
        /* noop */
      }
    })();
  }, []);

  const options = useMemo(() => {
    if (reqKind === "equipment") return catalogs.equipment;
    if (reqKind === "role") return catalogs.roles;
    return catalogs.certs;
  }, [reqKind, catalogs]);

  async function submitRequest(e) {
    e.preventDefault();
    if (!reqSubject) {
      toast.error("Pick what you want verified.");
      return;
    }
    try {
      await api("/me/contractor-profile/verification-requests", {
        method: "POST",
        body: {
          subject_kind: reqKind,
          subject_id: Number(reqSubject),
          requested_verifier_kind: reqVerifier,
        },
      });
      toast.success("Request submitted.");
      setReqSubject("");
      reload();
    } catch (err) {
      if (err?.code === "SELF_CLAIM_MISSING")
        toast.error("Self-declare the qualification first via the wizard.");
      else toast.error(err.message || "Request failed");
    }
  }

  if (!loaded) return null;

  return (
    <div data-testid={TID.myVerifBadgesPanel} className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">My Verifications</h2>
        <p className="text-sm text-slate-500">
          Verified rows live in dedicated tables. Self-declared claims remain
          unchanged when a verification arrives.
        </p>
      </div>

      <div className="avcp-card p-4 space-y-2">
        {rows.length === 0 ? (
          <div className="text-sm text-slate-500">
            No verifications yet. Request one from a manufacturer or ask a past
            Client to verify on a completed booking.
          </div>
        ) : (
          <div className="grid gap-2">
            {rows.map((r) => (
              <div
                key={`${r.kind}-${r.layer}-${r.row_id}`}
                data-testid={TID.myVerifRow}
                className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 py-2 text-sm"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="capitalize font-medium">{r.kind}</span>
                  <span className="text-slate-600">{r.label || r.slug}</span>
                  {r.project_count_tier && (
                    <span className="text-xs text-slate-500">· {r.project_count_tier}</span>
                  )}
                  <LayerBadge layer={r.layer} />
                  {r.revoked_at && (
                    <span className="text-xs text-rose-600 font-medium">REVOKED</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 text-right">
                  {r.verifier_display_name || "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <form
        onSubmit={submitRequest}
        className="avcp-card p-4 space-y-3"
      >
        <div>
          <h3 className="font-semibold">Request a verification</h3>
          <p className="text-xs text-slate-500">
            Sent to platform admins. You must self-declare the qualification first.
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="grid gap-1 text-sm">
            <span>Type</span>
            <select className="avcp-input" value={reqKind}
                    onChange={(e) => { setReqKind(e.target.value); setReqSubject(""); }}>
              {SUBJECT_KINDS.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm md:col-span-2">
            <span>Item</span>
            <select className="avcp-input" value={reqSubject}
                    onChange={(e) => setReqSubject(e.target.value)}>
              <option value="">— Select —</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>{o.label || o.display_name || o.slug}</option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm">
            <span>Verifier kind</span>
            <select className="avcp-input" value={reqVerifier}
                    onChange={(e) => setReqVerifier(e.target.value)}>
              <option value="manufacturer">Manufacturer</option>
              <option value="employer">Past Employer</option>
              <option value="platform_admin">Platform Admin</option>
              <option value="client">Past Client</option>
            </select>
          </label>
        </div>
        <div className="flex justify-end">
          <button type="submit" className="avcp-btn-primary"
                  data-testid={TID.myVerifRequestBtn}>
            Request verification
          </button>
        </div>
      </form>

      {requests.length > 0 && (
        <div className="avcp-card p-4 space-y-2">
          <div className="font-medium text-sm">Open & past requests</div>
          {requests.map((req) => (
            <div key={req.id} className="text-sm flex items-center justify-between gap-3 border-b border-slate-100 last:border-0 py-2">
              <div className="flex items-center gap-2">
                <span className="capitalize">{req.subject_kind}</span>
                <span className="text-slate-500">#{req.subject_id}</span>
                <span className="text-xs text-slate-500">
                  → {req.requested_verifier_kind}
                </span>
              </div>
              <div className="text-xs">
                {req.fulfilled_at && <span className="text-emerald-600">Fulfilled</span>}
                {req.declined_at && <span className="text-rose-600">Declined</span>}
                {!req.fulfilled_at && !req.declined_at && (
                  <span className="text-slate-500">Pending</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Verifications + Requests tabs
// ---------------------------------------------------------------------------

function AdminGrantForm({ onGranted }) {
  const [kind, setKind] = useState("equipment");
  const [contractorEmail, setContractorEmail] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [verifierKind, setVerifierKind] = useState("manufacturer");
  const [verifierName, setVerifierName] = useState("");
  const [tier, setTier] = useState("20+");
  const [evidence, setEvidence] = useState("");
  const [catalogs, setCatalogs] = useState({ equipment: [], roles: [], certs: [] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [eq, roles, certs] = await Promise.all([
          api("/catalogs/equipment-tags").catch(() => ({ data: [] })),
          api("/catalogs/roles").catch(() => ({ data: [] })),
          api("/catalogs/qualification-certifications").catch(() => ({ data: [] })),
        ]);
        setCatalogs({ equipment: eq.data || [], roles: roles.data || [], certs: certs.data || [] });
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const options = useMemo(() => {
    if (kind === "equipment") return catalogs.equipment;
    if (kind === "role") return catalogs.roles;
    return catalogs.certs;
  }, [kind, catalogs]);

  async function submit(e) {
    e.preventDefault();
    if (!contractorEmail || !subjectId || !verifierName) {
      toast.error("Contractor email, item, and verifier display name are required.");
      return;
    }
    setSubmitting(true);
    try {
      // Look up user by email via admin users list
      const usersResp = await api(`/admin/users?email=${encodeURIComponent(contractorEmail)}`)
        .catch(() => ({ data: [] }));
      const user = (usersResp.data || []).find(
        (u) => u.email?.toLowerCase() === contractorEmail.toLowerCase()
      );
      if (!user) {
        toast.error("Contractor not found. Use exact email.");
        return;
      }
      const body = {
        contractor_user_id: user.id,
        subject_id: Number(subjectId),
        verifier_kind: verifierKind,
        verifier_display_name: verifierName,
      };
      if (evidence) body.evidence_url = evidence;
      if (kind === "equipment") body.project_count_tier = tier;
      await api(`/admin/verify/${kind}`, { method: "POST", body });
      toast.success("Verification granted.");
      setContractorEmail("");
      setSubjectId("");
      setVerifierName("");
      setEvidence("");
      onGranted?.();
    } catch (err) {
      if (err?.code === "SELF_CLAIM_MISSING")
        toast.error("Contractor hasn't self-declared this qualification.");
      else toast.error(err.message || "Grant failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit}
          data-testid={TID.adminVerifGrantForm}
          className="avcp-card p-4 space-y-3">
      <div className="font-semibold">Grant company-verified row</div>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="grid gap-1 text-sm">
          <span>Subject kind</span>
          <select className="avcp-input" value={kind}
                  onChange={(e) => { setKind(e.target.value); setSubjectId(""); }}>
            {SUBJECT_KINDS.map((k) => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          <span>Contractor email</span>
          <input className="avcp-input" value={contractorEmail}
                 onChange={(e) => setContractorEmail(e.target.value)}
                 placeholder="lead.demo@…" />
        </label>
        <label className="grid gap-1 text-sm">
          <span>Item</span>
          <select className="avcp-input" value={subjectId}
                  onChange={(e) => setSubjectId(e.target.value)}>
            <option value="">— Select —</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>{o.label || o.display_name || o.slug}</option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          <span>Verifier kind</span>
          <select className="avcp-input" value={verifierKind}
                  onChange={(e) => setVerifierKind(e.target.value)}>
            <option value="manufacturer">Manufacturer</option>
            <option value="employer">Employer</option>
            <option value="platform_admin">Platform Admin</option>
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          <span>Verifier display name</span>
          <input className="avcp-input" value={verifierName}
                 onChange={(e) => setVerifierName(e.target.value)}
                 placeholder="Barco / AVI-SPL / Workforce Partners" />
        </label>
        {kind === "equipment" && (
          <label className="grid gap-1 text-sm">
            <span>Project count tier</span>
            <select className="avcp-input" value={tier}
                    onChange={(e) => setTier(e.target.value)}>
              {TIERS.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
          </label>
        )}
        <label className="grid gap-1 text-sm md:col-span-3">
          <span>Evidence URL (optional)</span>
          <input className="avcp-input" value={evidence}
                 onChange={(e) => setEvidence(e.target.value)}
                 placeholder="https://…" />
        </label>
      </div>
      <div className="flex justify-end">
        <button type="submit" className="avcp-btn-primary"
                data-testid={TID.adminVerifGrantBtn}
                disabled={submitting}>
          {submitting ? "Granting…" : "Grant verification"}
        </button>
      </div>
    </form>
  );
}

export function AdminVerificationsTab() {
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);

  async function reload() {
    try {
      const [v, r] = await Promise.all([
        api("/admin/verifications?limit=100"),
        api("/admin/verification-requests?limit=100"),
      ]);
      setRows(v.data || []);
      setRequests(r.data || []);
    } catch (err) {
      toast.error(err.message || "Load failed");
    }
  }

  useEffect(() => { reload(); }, []);

  async function revoke(row) {
    const reason = window.prompt(`Reason for revoking ${row.kind} #${row.row_id}?`);
    if (!reason) return;
    try {
      await api(`/admin/verifications/${row.kind}/${row.layer}/${row.row_id}/revoke`, {
        method: "POST", body: { reason },
      });
      toast.success("Revoked.");
      reload();
    } catch (err) {
      toast.error(err.message || "Revoke failed");
    }
  }

  async function declineRequest(req) {
    const reason = window.prompt(`Decline reason for request #${req.id}?`);
    if (!reason) return;
    try {
      await api(`/admin/verification-requests/${req.id}/decline`, {
        method: "POST", body: { reason },
      });
      toast.success("Request declined.");
      reload();
    } catch (err) {
      toast.error(err.message || "Decline failed");
    }
  }

  return (
    <div data-testid={TID.adminVerifTab} className="space-y-6">
      <AdminGrantForm onGranted={reload} />

      <div className="avcp-card p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-semibold">Recent verifications</div>
          <button className="text-sm text-slate-600 hover:underline" onClick={reload}>
            Refresh
          </button>
        </div>
        <table data-testid={TID.adminVerifTable} className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b">
              <th className="py-1">Kind</th>
              <th>Layer</th>
              <th>Contractor</th>
              <th>Subject</th>
              <th>Verifier</th>
              <th>Verified at</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.kind}-${r.layer}-${r.row_id}`}
                  data-testid={TID.adminVerifRow}
                  className="border-b last:border-0">
                <td className="py-2 capitalize">{r.kind}</td>
                <td><LayerBadge layer={r.layer} /></td>
                <td className="text-xs">#{r.contractor_user_id}</td>
                <td className="text-xs">
                  #{r.subject_id}{r.project_count_tier ? ` · ${r.project_count_tier}` : ""}
                </td>
                <td className="text-xs">
                  {r.verifier_display_name || `#${r.verifier_user_id}`}
                  {r.verifier_kind ? ` (${r.verifier_kind})` : ""}
                </td>
                <td className="text-xs">{r.verified_at?.slice(0, 19)}</td>
                <td>
                  {!r.revoked_at && (
                    <button
                      data-testid={TID.adminVerifRevokeBtn}
                      className="text-rose-600 text-xs hover:underline"
                      onClick={() => revoke(r)}
                    >
                      Revoke
                    </button>
                  )}
                  {r.revoked_at && (
                    <span className="text-xs text-slate-400">revoked</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-500 py-3">
                No verifications yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div data-testid={TID.adminVerifRequestsTab}
           className="avcp-card p-4 space-y-2">
        <div className="font-semibold">Verification requests</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b">
              <th className="py-1">ID</th>
              <th>Kind</th>
              <th>Subject</th>
              <th>From</th>
              <th>→ Verifier</th>
              <th>Requested</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {requests.map((req) => (
              <tr key={req.id}
                  data-testid={TID.adminVerifRequestRow}
                  className="border-b last:border-0">
                <td className="py-2">#{req.id}</td>
                <td className="capitalize">{req.subject_kind}</td>
                <td className="text-xs">#{req.subject_id}</td>
                <td className="text-xs">{req.requested_by_email}</td>
                <td className="text-xs">{req.requested_verifier_kind}</td>
                <td className="text-xs">{req.requested_at?.slice(0, 19)}</td>
                <td className="text-xs">
                  {req.fulfilled_at && <span className="text-emerald-600">Fulfilled</span>}
                  {req.declined_at && <span className="text-rose-600">Declined</span>}
                  {!req.fulfilled_at && !req.declined_at && (
                    <span className="text-slate-500">Pending</span>
                  )}
                </td>
                <td>
                  {!req.fulfilled_at && !req.declined_at && (
                    <button
                      className="text-rose-600 text-xs hover:underline"
                      onClick={() => declineRequest(req)}
                    >
                      Decline
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-3">
                No verification requests.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
